import React, { useState } from 'react';
import './App.css';

const App = () => {
  const [currentPage, setCurrentPage] = useState('home');

  const navigate = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ============================================
  // HOME PAGE (Hero + About + Contact)
  // ============================================
  const HomePage = () => {
    return (
      <>
        {/* HERO SECTION */}
        <section className="hero-section">
          <div className="hero-container">
            {/* Profile Photo */}
            <div className="hero-photo-wrap">
              <img
                src="/images/profile.jpg"
                alt="Emmanuela Omondi"
                className="hero-photo"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://placehold.co/400x400/f5deb3/008080?text=EO';
                }}
              />
              <div className="hero-photo-badge">
                <span className="status-dot"></span>
                Available for Work
              </div>
            </div>

            {/* Hero Content */}
            <div className="hero-content">
              <h1 className="hero-title">
                Emmanuela Omondi
              </h1>
              <p className="hero-subtitle">Award-Winning Software Engineer | Building Real-World Solutions</p>
              <p className="hero-description">
                I transform complex challenges into practical digital solutions across 
                <strong> web, mobile, AI, cloud, and IoT</strong>. From fintech to agritech, 
                I create scalable applications that solve real problems and deliver measurable impact.
              </p>

              {/* Quick Stats */}
              <div className="hero-stats">
                <div className="stat-item">
                  <div className="stat-value">3+</div>
                  <div className="stat-label">Years</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">20+</div>
                  <div className="stat-label">Projects</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">15+</div>
                  <div className="stat-label">Clients</div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="hero-actions">
                <button onClick={() => navigate('projects')} className="cta-button">
                  View My Work
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </button>
                <a
                  href={process.env.PUBLIC_URL + '/EmmanuelaOmondi.pdf'}
                  download="EmmanuelaOmondi.pdf"
                  className="cv-download-link"
                >
                  Download CV
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ABOUT SECTION */}
        <section className="about-section">
          <div className="container">
            <h2 className="section-title">About Me</h2>
            <div className="about-content">
              <p>
                I am <strong>Emmanuela Omondi</strong>, an award-winning Software Engineer passionate about building 
                intelligent, scalable, and impactful digital solutions. My expertise spans full-stack development, 
                artificial intelligence, cloud technologies, backend engineering, and IoT, with a focus on creating 
                products that solve real-world challenges across industries such as fintech, agritech, and business automation.
              </p>
              <p>
                I enjoy transforming complex ideas into reliable, user-centered applications—from AI-powered platforms 
                and cloud-native systems to modern web and mobile experiences. I believe great software is not only 
                functional but also intuitive, secure, and built to scale.
              </p>
              <p>
                Driven by curiosity and continuous learning, I embrace emerging technologies and collaborate with diverse 
                teams to deliver innovative solutions that create measurable value. My mission is to build technology that 
                <strong> empowers businesses, improves lives, and shapes the future through innovation</strong>.
              </p>
            </div>

            {/* Skills Grid */}
            <div className="skills-section">
              <h3 className="skills-title">Technical Expertise</h3>
              <div className="skills-grid">
                <div className="skill-category">
                  <h4>Frontend</h4>
                  <p>React.js, Next.js, JavaScript, TypeScript, HTML5, CSS3</p>
                </div>
                <div className="skill-category">
                  <h4>Backend</h4>
                  <p>Node.js, Fastify, PHP, Python, REST APIs, GraphQL</p>
                </div>
                <div className="skill-category">
                  <h4>Mobile & IoT</h4>
                  <p>Flutter, React Native, Arduino, Raspberry Pi, MQTT</p>
                </div>
                <div className="skill-category">
                  <h4>Cloud & DevOps</h4>
                  <p>AWS, Docker, Kubernetes, CI/CD, Terraform</p>
                </div>
                <div className="skill-category">
                  <h4>Database</h4>
                  <p>MySQL, PostgreSQL, Firebase, Supabase</p>
                </div>
                <div className="skill-category">
                  <h4>Tools</h4>
                  <p>Git / GitHub, Figma, VS Code, Agile</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CONTACT SECTION */}
        <section className="contact-section">
          <div className="container">
            <h2 className="section-title">Let's Work Together</h2>
            <p className="contact-intro">
              Have a project in mind? I'd love to hear about it. Whether you have a full brief or just a rough idea, 
              let's discuss how we can bring your vision to life.
            </p>

            <div className="contact-grid">
              {/* Contact Info */}
              <div className="contact-info">
                <div className="contact-item">
                  <div className="contact-icon"></div>
                  <div>
                    <div className="contact-label">Email</div>
                    <a href="mailto:omondiemmanuela7@gmail.com" className="contact-value">
                      omondiemmanuela7@gmail.com
                    </a>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon"></div>
                  <div>
                    <div className="contact-label">Location</div>
                    <div className="contact-value">Nairobi, Kenya 🇰🇪</div>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon"></div>
                  <div>
                    <div className="contact-label">Response Time</div>
                    <div className="contact-value">Within 24 hours</div>
                  </div>
                </div>

                {/* Social Links */}
                <div className="social-links">
                  <a href="https://github.com/Emmanuela-dev/" target="_blank" rel="noopener noreferrer" className="social-btn" title="GitHub">
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                    </svg>
                  </a>
                  <a href="https://www.linkedin.com/in/emmanuela-omondi-a7a7032b9" target="_blank" rel="noopener noreferrer" className="social-btn" title="LinkedIn">
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                    </svg>
                  </a>
                  <a href="https://wa.me/254793540542" target="_blank" rel="noopener noreferrer" className="social-btn" title="WhatsApp">
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </a>
                  <a href="https://twitter.com/EmmanuelaOmondi" target="_blank" rel="noopener noreferrer" className="social-btn" title="Twitter">
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a>
                </div>
              </div>

              {/* Contact Form */}
              <div className="contact-form">
                <h3>Send a Message</h3>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const text = `Hi Emmanuela, I'm ${formData.get('name')}.\n\n${formData.get('message')}\n\nReply to: ${formData.get('email')}`;
                  window.open(`https://wa.me/254793540542?text=${encodeURIComponent(text)}`, '_blank');
                  e.target.reset();
                }}>
                  <div className="form-row">
                    <input type="text" name="name" placeholder="Your Name" required />
                    <input type="email" name="email" placeholder="Your Email" required />
                  </div>
                  <textarea name="message" placeholder="Your Message" rows="5" required></textarea>
                  <button type="submit" className="submit-btn">Send Message</button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  };

  // ============================================
  // PROJECTS PAGE
  // ============================================
  const ProjectsPage = () => {
    const projects = [
      {
        id: 1,
        title: 'AI Resume Builder',
        description: 'An AI-powered resume builder that generates tailored, ATS-optimized resumes. Users pick a template, answer guided prompts, and get a polished resume with real-time preview.',
        category: 'Web Application',
        image: '/images/resume-builder.jpeg',
        live: 'https://resumebuilder-red-rho.vercel.app/',
        github: 'https://github.com/Emmanuela-dev/Resumebuilder',
      },
      {
        id: 2,
        title: 'GreenSmart Farm Management',
        description: 'Full-stack web platform for modern farm operations — crop tracking, livestock monitoring, inventory management, and weather integration. Helps farmers make data-driven decisions at scale.',
        category: 'Web Application',
        image: '/images/sam.jpeg',
        live: 'https://greensmart-investments.vercel.app/',
        github: 'https://github.com/Emmanuela-dev/Emmanuela-github.io',
      },
      {
        id: 3,
        title: 'Epique Interiors',
        description: 'A visually immersive portfolio website for an interior design studio. Features a dynamic gallery, animated service showcase, client testimonials, and contact forms with email notifications.',
        category: 'Web Application',
        image: '/images/interior.png',
        live: 'http://silapainteriors.free.nf/Epique-Interiors-main/index.php',
        github: 'https://github.com/Emmanuela-dev/Epique-Interiors',
      },
      {
        id: 4,
        title: 'FarmCred AI',
        description: 'AI-powered credit scoring platform for smallholder farmers who lack traditional credit history. Analyzes farm productivity data, weather patterns, and market trends to generate creditworthiness scores.',
        category: 'Mobile Application',
        image: '/images/farmcred.png',
        live: null,
        github: null,
      },
      {
        id: 5,
        title: 'Soil Monitoring System',
        description: 'AI-powered soil monitoring platform that leverages IoT sensors to track moisture levels, pH, temperature, and nutrient content in real-time. Features predictive analytics for crop recommendations.',
        category: 'IoT / Web Application',
        image: '/images/soil monitoring.jpeg',
        live: null,
        github: null,
      },
      {
        id: 6,
        title: 'MamaPesa',
        description: 'Mobile-first personal finance app built for women in emerging markets. Helps users track savings, set financial goals, join group savings (chamas), and access micro-finance resources.',
        category: 'Mobile Application',
        image: '/images/mamapesa.jpeg',
        live: null,
        github: null,
      },
    ];

    return (
      <section className="projects-page">
        <div className="container">
          {/* Header with Back Button */}
          <div className="projects-header">
            <button onClick={() => navigate('home')} className="back-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              Back to Home
            </button>
            <h1 className="page-title">My Work</h1>
            <p className="page-subtitle">
              Real projects solving real problems. From AI-powered platforms to IoT systems, 
              each project represents a commitment to quality and impact.
            </p>
          </div>

          {/* Projects Grid */}
          <div className="projects-grid">
            {projects.map((project) => (
              <div key={project.id} className="project-card">
                <div className="project-image">
                  <img
                    src={project.image}
                    alt={project.title}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://placehold.co/600x400/f5deb3/008080?text=Project';
                    }}
                  />
                  <div className="project-category">{project.category}</div>
                </div>
                <div className="project-content">
                  <h3 className="project-title">{project.title}</h3>
                  <p className="project-description">{project.description}</p>
                  <div className="project-links">
                    {project.live && (
                      <a href={project.live} target="_blank" rel="noopener noreferrer" className="project-btn">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                        </svg>
                        Live Demo
                      </a>
                    )}
                    {project.github && (
                      <a href={project.github} target="_blank" rel="noopener noreferrer" className="project-btn secondary">
                        <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                        </svg>
                        View Code
                      </a>
                    )}
                    {!project.live && !project.github && (
                      <span className="project-status">In Development</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <div className="app">
      {/* Navigation Bar */}
      <nav className="navbar">
        <div className="nav-container">
          <button onClick={() => navigate('home')} className="logo">
            E. Omondi
          </button>
          <div className="nav-links">
            <button 
              onClick={() => navigate('home')} 
              className={currentPage === 'home' ? 'active' : ''}
            >
              Home
            </button>
            <button 
              onClick={() => navigate('projects')} 
              className={currentPage === 'projects' ? 'active' : ''}
            >
              Projects
            </button>
          </div>
        </div>
      </nav>

      {/* Page Content */}
      <main className="main-content">
        {currentPage === 'home' ? <HomePage /> : <ProjectsPage />}
      </main>

      {/* Footer */}
      <footer className="footer">
        <p>&copy; 2025 Emmanuela Omondi. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default App;
